import React, { useEffect, useState, useCallback, useRef } from "react"
import { NavLink, useParams } from "react-router-dom"

import { gql, useQuery, useApolloClient } from "@apollo/client"
import { useDispatch, useSelector } from "react-redux"
import { setUser } from "_actions/user-actions"
import { toggleFilterMenu } from "_actions/menu-actions"

import styled from "styled-components"
import { isEqual } from "lodash/fp"

import { CLIENT_RECRUITMENTS_STATS, DASHBOARD } from "_containers/recruitment/recruitment-ql"
import { COMPANY_CONTACTS } from "../settings/company-profile-ql"

import { UserRoles, HAS_SUBSCRIPTION } from "_root/constants"
import * as url from "_routes/url-names"
import dashboardFilter from "_utils/dashboardFilter"

import MainLayout from "_layout/main-layout"
import Filter from "_components/dashboard/filter"
import RecruitmentList from "_components/dashboard/recruitment-list"
import { SmallSpinner } from "_root/layout/form-elements"
import { PrimaryLinkButton } from "_root/layout/buttons"

import ArrowDownIcon from "_images/menu-down.svg"
import ProfileImage from "_images/profile.svg"
import MailIconSrc from "_images/email.svg"

//#region Styles
const Wrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  height: 100%;
`
const Welcome = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 25px;
  padding: 24px 30px 20px 30px;
  border: 1px solid var(--color-bg-light);
  box-shadow: 0px 0px 5px var(--color-bg-light);
  background: var(--color-bg-white);

  @media screen and (max-width: 767px) {
    align-items: flex-start;
    flex-direction: column;
    margin-bottom: 10px;
    padding: 10px;
  }
`

const RecruitButtons = styled.div`
  padding-top: 2rem;

  @media screen and (max-width: 767px) {
    margin-top: 10px;
  }
`
const Hello = styled.div`
  .greeting {
    display: block;
    font-size: 30px;
    line-height: 34px;
  }

  @media screen and (max-width: 767px) {
    .greeting {
      font-size: 20px;
      line-height: 24px;
    }
  }
`
const Contacts = styled.div`
  display: flex;
  align-items: flex-start;
  margin-left: auto;
  white-space: nowrap;

  @media screen and (max-width: 767px) {
    display: none;
  }
`
const Bubble = styled.div`
  position: relative;
  display: none;
  padding: 1rem;
  margin-right: 1rem;
  border-radius: 2rem;
  border: 1px solid var(--color-line);
  box-shadow: 1px 1px 1px var(--color-line);
  max-width: 200px;
  font-size: 0.9rem;
  white-space: initial;

  &:after {
    content: '';
    position: absolute;
    top: 40%;
    right: -10px;
    width: 0;
    height: 0;
    border: 10px solid transparent;
    border-left-color: #fff;
    border-right: 0;
    margin-top: -10px;
  }

  &:before {
    content: '';
    position: absolute;
    top: 40%;
    right: -13px;
    width: 0;
    height: 0;
    border: 11px solid transparent;
    border-left-color: var(--color-line);
    border-right: 0;
    margin-top: -11px;
  }

  @media screen and (min-width: 768px) {
    display: inline-block;
    max-width: 300px;
  }

  @media screen and (min-width: 1500px) {
    max-width: 500px;
  }
`
const Contact = styled.div`
  display: inline-flex;
  margin: 0 10px;
  text-align: center;
  align-items: center;
  flex-flow: column nowrap;
`
const ContactImage = styled.div`
  display: inline-block;
  width: 80px;
  height: 80px;
  margin-bottom: 5px;
  border: 1px solid var(--color-text);
  background: url(${ProfileImage}) no-repeat scroll 50% 50%;
  background-size: 34px 34px;

  img {
    max-width: 100%;
  }
`
const ContactLink = styled.a`
  display: inline-block;
  color: var(--color-text);
  white-space: nowrap;
`
const MailIcon = styled.i`
  display: inline-block;
  margin-right: 4px;
  width: 20px;
  height: 12px;
  background: url(${MailIconSrc}) no-repeat scroll 50% 50%;
`
const Header = styled(NavLink)`
  display: flex;
  color: var(--color-text-white);
  padding: 5px 20px;
  margin: 2px 0;
  background: var(--color-nav-3);
  white-space: nowrap;

  &.active {
    pointer-events: none;
  }

  &:hover {
    text-decoration: none;
  }

  .count {
    font-size: 0.8rem;
    color: var(--color-bg-grey-dark);
  }

  .spinner-container {
    margin-left: 10px;
  }

  @media (max-height: 500px) {
    padding: 7px 20px;

    span {
      font-size: 14px;
    }
  }
`
const ArrowDown = styled.span`
  display: inline-block;
  width: 20px;
  height: 24px;
  margin-left: auto;
  background: url(${ArrowDownIcon}) no-repeat scroll 50% 50%;

  @media (max-height: 500px) {
    width: 20px;
  }
`
const ArrowUp = styled(ArrowDown)`
  transform: rotate(180deg);
`
const FetchButton = styled.button`
  pointer-events: all;
  padding: 0 10px;
  margin-left: 10px;
  height: 24px;
  color: var(--color-bg-grey);
  font-size: 12px;
  border: 1px solid var(--color-bg-grey-dark);
  border-radius: 12px;
  background: transparent;
  outline: none;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  white-space: nowrap;

  &:hover {
    background: var(--color-brand-black-hover);
  }
`
const Content = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  padding: 35px 40px 30px 40px;
  min-width: 1080px;

  @media screen and (max-width: 767px) {
    padding: 10px 0;
    min-width: 0;
  }

  @media screen and (max-width: 1280px) {
    padding: 20px;
  }

  @media screen and (max-height: 650px) {
    padding: 10px 20px 10px 10px;
    ${Welcome} {
      margin-bottom: 10px;
      padding: 10px 20px;

      ${Hello} {
        .greeting {
          display: none;
        }
      }
      ${RecruitButtons} {
        padding-top:0;
      }
      ${Bubble} {
        display:none;
      }
      ${Contact} {
        flex-flow: row;
        ${ContactImage}{
          width:55px;
          height:55px;
          margin:0 10px 0 0;
          
        }
      }
    }
    .content {
      .date {
        padding:3px 0;
      }
    }
  }
`
        
//#endregion
        
const Overview = () => {
  const BATCH_SIZE = 50

  const { company, state: currentView } = useParams()
  const client = useApolloClient()
  const dispatch = useDispatch()

  const user = useSelector((state) => state.user)
  const menu = useSelector((state) => state.menu)
  
  const hasCheckedInitialData = useRef(false)
  
  const [canFetchMore, setCanFetchMore] = useState({
    active: false,
    draft: false,
    closed: false
  })
  
  const [fetchingStates, setFetchingStates] = useState({
    active: false,
    draft: false,
    closed: false
  })
  
  const dashboardQueryConfig = {
    fetchPolicy: "network-only",
    variables: {
      take: BATCH_SIZE,
      skip: 0,
      companyId: user.companyView.id,
    }
  }

  const { data: companyContactsData } = useQuery(COMPANY_CONTACTS, {
    variables: { urlName: company },
  })
  
  const { data: recruitmentStats, loading: clientRecruitmentsLoading } = useQuery(CLIENT_RECRUITMENTS_STATS,
    { fetchPolicy: "cache-only" }
  )
  
  const shouldFetchDashboard = (view) => {
    if (!currentView) return view === 'AKTIVA'
    
    return currentView.toUpperCase() === view.toUpperCase()
  } 

  const {
    data: activeData,
    loading: activeLoading,
    fetchMore: fetchMoreActive,
    refetch: refetchActive
  } = useQuery(DASHBOARD, {
    ...dashboardQueryConfig,
    skip: !shouldFetchDashboard("AKTIVA"),
    variables: {
      ...dashboardQueryConfig.variables,
      where: dashboardFilter(user.filter, "ACTIVE", user.companyView),
    }
  })
  
  const {
    data: draftData,
    loading: draftLoading,
    fetchMore: fetchMoreDraft,
    refetch: refetchDraft
  } = useQuery(DASHBOARD, {
    ...dashboardQueryConfig,
    skip: !shouldFetchDashboard("UTKAST"),
    variables: {
      ...dashboardQueryConfig.variables,
      where: dashboardFilter(user.filter, "DRAFT", user.companyView),
    }
  })
  
  const {
    data: closedData,
    loading: closedLoading,
    fetchMore: fetchMoreClosed,
    refetch: refetchClosed
  } = useQuery(DASHBOARD, {
    ...dashboardQueryConfig,
    skip: !shouldFetchDashboard("AVSLUTADE"),
    variables: {
      ...dashboardQueryConfig.variables,
      where: dashboardFilter(user.filter, "CLOSED", user.companyView),
    }
  })
  
  const recruitmentsActive = activeData?.dashboardRecruitments || []
  const recruitmentsDraft = draftData?.dashboardRecruitments || []
  const recruitmentsClosed = closedData?.dashboardRecruitments || []
  
  const hasSubscription = user?.companyView?.subscription === HAS_SUBSCRIPTION
  
  const userName = `${user.firstName} ${user.lastName}`
  const view = `/${currentView}`
  
  const isActiveView = view === url.REK_AKTIVA
  const isDraftView = view === url.REK_UTKAST
  const isClosedView = view === url.REK_AVSLUTADE
  
  const activeCount = Number.isInteger(menu.activeCount) && (isActiveView ? `${recruitmentsActive.length} av ${menu.activeCount}` : `${menu.activeCount}`)
  const draftCount = Number.isInteger(menu.draftCount) && (isDraftView ? `${recruitmentsDraft.length} av ${menu.draftCount}` : `${menu.draftCount}`)
  const closedCount = Number.isInteger(menu.closedCount) && (isClosedView ? `${recruitmentsClosed.length} av ${menu.closedCount}` : `${menu.closedCount}`)
  
  const hasWriteAccess = user.superAdmin ||
  (hasSubscription && ![UserRoles.GROUPVIEWER, UserRoles.view].includes(user.role))
  
  const checkForStaleDataAndRefetch = useCallback((currentView, checkLoaded) => {
    if (checkLoaded) {
      if (activeData && !activeLoading) {
        refetchActive()
      }
      if (draftData && !draftLoading) {
        refetchDraft()
      }
      if (closedData && !closedData) {
        refetchClosed()
      }
    }
      
    if (clientRecruitmentsLoading) return

    let updateStaleKey
    if (url.REK_AKTIVA.indexOf(currentView) >= 0 && recruitmentStats?.activeRecruitmentsIsStale) {
      refetchActive && refetchActive()
      updateStaleKey = "activeRecruitmentsIsStale"
    }
    if (url.REK_UTKAST.indexOf(currentView) >= 0 && recruitmentStats?.draftRecruitmentsIsStale) {
      refetchDraft && refetchDraft()
      updateStaleKey = "draftRecruitmentsIsStale"
    }
    if (url.REK_AVSLUTADE.indexOf(currentView) >= 0 && recruitmentStats?.closedRecruitmentsIsStale) {
      refetchClosed && refetchClosed()
      updateStaleKey = "closedRecruitmentsIsStale"
    }
  
    if (updateStaleKey) {
      client.writeQuery ({
        query: gql`
          query RecruitmentStats {
            recruitmentStats {
              countRecruitmentsIsStale
              activeRecruitmentsIsStale
              draftRecruitmentsIsStale
              closedRecruitmentsIsStale
            }
          }
        `,
        data: {
          recruitmentStats
        },
      })
    }
  }, [
    activeData, activeLoading, refetchActive,
    draftData, draftLoading, refetchDraft,
    closedData, closedLoading, refetchClosed,
    clientRecruitmentsLoading, recruitmentStats,
    client
  ])

  const fetchActive = () => {
    const dashboardRecruitments = activeData?.dashboardRecruitments
    if (!dashboardRecruitments || activeLoading || fetchingStates.active) return

    setFetchingStates(prev => ({ ...prev, active: true }))

    fetchMoreActive ({
      variables: { skip: dashboardRecruitments.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        setFetchingStates(prev => ({ ...prev, active: false }))
        setCanFetchMore(prev => ({
          ...prev,
          active: fetchMoreResult && fetchMoreResult.dashboardRecruitments.length === BATCH_SIZE
        }))

        if (!fetchMoreResult) return prev
        return {
          dashboardRecruitments: [
            ...prev.dashboardRecruitments,
            ...fetchMoreResult.dashboardRecruitments
          ]
        }
      }
    })
  }

  const fetchDraft = () => {
    const dashboardRecruitments = draftData?.dashboardRecruitments
    if (!dashboardRecruitments || draftLoading || fetchingStates.draft) return

    setFetchingStates(prev => ({ ...prev, draft: true}))

    fetchMoreDraft ({
      variables: { skip: dashboardRecruitments.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        setFetchingStates( prev => ({ ...prev, draft: false}))
        setCanFetchMore(prev => ({
          ...prev,
          draft: fetchMoreResult && fetchMoreResult.dashboardRecruitments.length === BATCH_SIZE
        }))

        if (!fetchMoreResult) return prev
        return {
          dashboardRecruitments: [
            ...prev.dashboardRecruitments,
            ...fetchMoreResult.dashboardRecruitments
          ]
        }
      }
    })
  }

  const fetchClosed = () => {
    const dashboardRecruitments = closedData?.dashboardRecruitments
    if (!dashboardRecruitments || closedLoading || fetchingStates.closed) return

    setFetchingStates(prev => ({ ...prev, closed: true}))

    fetchMoreClosed ({
      variables: { skip: dashboardRecruitments.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        setFetchingStates( prev => ({ ...prev, closed: false}))
        setCanFetchMore(prev => ({
          ...prev,
          closed: fetchMoreResult && fetchMoreResult.dashboardRecruitments.length === BATCH_SIZE
        }))

        if (!fetchMoreResult) return prev
        return {
          dashboardRecruitments: [
            ...prev.dashboardRecruitments,
            ...fetchMoreResult.dashboardRecruitments
          ]
        }
      }
    })
  }
  
  const toggleFilter = () => dispatch(toggleFilterMenu())
  
  const onFilter = (filter) => {
    if (isEqual(filter, user.filter)) return
    
    setCanFetchMore({
      active: false,
      draft: false,
      closed: false
    })
    
    dispatch(setUser({ ...user, filter}))
  }
  
  // Checks and updates stale data
  useEffect(() => {
    checkForStaleDataAndRefetch(currentView, !hasCheckedInitialData.current)
    hasCheckedInitialData.current = true
  }, [currentView, checkForStaleDataAndRefetch])
  
  // Checking if each section has reached its data limit
  useEffect(() => {
    if (activeData && !activeLoading && activeData.dashboardRecruitments) {
      setCanFetchMore(prev => ({
        ...prev,
        active: activeData.dashboardRecruitments.length >= BATCH_SIZE
      }))
    }
    
    if (draftData && !draftLoading && draftData.dashboardRecruitments) {
      setCanFetchMore(prev => ({
        ...prev,
        draft: draftData.dashboardRecruitments.length >= BATCH_SIZE
      }))
    }

    if (closedData && !closedLoading && closedData.dashboardRecruitments) {
      setCanFetchMore(prev => ({
        ...prev,
        closed: closedData.dashboardRecruitments.length >= BATCH_SIZE
      }))
    }
  }, [activeData, activeLoading, draftData, draftLoading, closedData, closedLoading])
  
  const activeSpinner = activeLoading && <SmallSpinner />
  const draftSpinner = draftLoading && <SmallSpinner />
  const closedSpinner = closedLoading && <SmallSpinner />
  
  const fetchMoreActiveButton = isActiveView &&
    !activeSpinner &&
    !!activeCount &&
    recruitmentsActive.length < menu.activeCount &&
    canFetchMore.active && (
      <FetchButton
        onClick={fetchActive}
        disabled={fetchingStates.active}
      >
        Hämta fler {fetchingStates.active && <SmallSpinner />}
      </FetchButton>
    )
  const fetchMoreDraftButton = isDraftView &&
    !draftSpinner &&
    !!activeCount &&
    recruitmentsDraft.length < menu.draftCount &&
    canFetchMore.draft && (
      <FetchButton
        onClick={fetchDraft}
        disabled={fetchingStates.draft}
      >
        Hämta fler {fetchingStates.draft && <SmallSpinner />}
      </FetchButton>
    )

  const fetchMoreClosedButton = isClosedView &&
    !closedSpinner &&
    !!activeCount &&
    recruitmentsClosed.length < menu.closedCount &&
    canFetchMore.closed && (
      <FetchButton
        onClick={fetchClosed}
        disabled={fetchingStates.closed}
      >
        Hämta fler {fetchingStates.closed && <SmallSpinner />}
      </FetchButton>
    )

  return (
    <MainLayout maxHeight row>
      <Wrapper>
        <Content>
          <Welcome>
            <Hello>
              <strong className="greeting">Hej {userName}!</strong>
              <RecruitButtons>
                {hasWriteAccess && (
                  <PrimaryLinkButton className="hello-action-button" to={`/${company}${url.REKRYTERING}${url.REK_NY}`}>
                    Skapa ny rekrytering
                  </PrimaryLinkButton>)}
              </RecruitButtons>
            </Hello>

            {companyContactsData?.company?.jobbetContacts?.length > 0 && (
              <Contacts>
                {!hasSubscription && menu.activeCount > 0 &&
                  <Bubble>Vi bjuder alltid på fri tillgång till vårt Rekryteringscenter under 90 dagar i samband med att du bokar en tjänst via Jobbet.se. Fri support ingår vardagar 08-17.</Bubble>
                }
                {!hasSubscription && menu.activeCount == 0 &&
                  <Bubble>Ert företag saknar avtal med Jobbet.se och du kan därför inte se dina tidigare rekryteringar eller aktivera en ny rekrytering.
                    Kontakta kundtjanst@jobbet.se för information om hur ni aktiverar ert konto/tecknar avtal.</Bubble>
                }
                {hasSubscription &&
                  <Bubble>Vi finns här för att hjälpa dig med din rekrytering!<br />I din tjänst ingår fri support vardagar 08-17. Ring eller maila oss gärna!</Bubble>
                }

                {companyContactsData.company.jobbetContacts.map((contact) => (
                  <Contact key={contact.id}>
                    <ContactImage data-tooltip-id="root-tooltip" data-tooltip-content={contact.firstName + " " + contact.lastName}>
                      {contact.picture && contact.picture.url && <img src={contact.picture.url} alt={contact.firstName + " " + contact.lastName} />}
                    </ContactImage>
                    <span>
                      {contact.email && (
                        <ContactLink href={"mailto:" + contact.email} data-tooltip-id="root-tooltip" data-tooltip-content={contact.email}>
                          <MailIcon />
                          {contact.firstName + " " + contact.lastName}
                        </ContactLink>
                      )}
                      <br />

                      {contact.phone || contact.mobile ? (
                        <ContactLink href={"tel:" + contact.mobile}>{contact.mobile}</ContactLink>
                      ) : null}
                    </span>
                  </Contact>
                ))}
              </Contacts>
            )}
          </Welcome>

          <Header to={`/${company}${url.REKRYTERING}${url.REK_AKTIVA}`}>
            <span>
              Aktiva rekryteringar {activeCount && <span className="count">({activeCount})</span>}
            </span>
            {fetchMoreActiveButton}
            {activeSpinner}
            {view === url.REK_AKTIVA ? <ArrowDown /> : <ArrowUp />}
          </Header>
          {isActiveView && (
            <RecruitmentList
              recruitments={activeData?.dashboardRecruitments || []}
              loading={activeLoading}
            />
          )}

          <Header
            to={`/${company}${url.REKRYTERING}${url.REK_UTKAST}`}
          >
            <span>Utkast till rekryteringar {draftCount && <span className="count">({draftCount})</span>}</span>
            {fetchMoreDraftButton}
            {draftSpinner}
            {view === url.REK_UTKAST ? <ArrowDown /> : <ArrowUp />}
          </Header>
          {isDraftView && (
            <RecruitmentList
              recruitments={draftData?.dashboardRecruitments || []}
              loading={draftLoading}
            />
          )}

          <Header
            to={`/${company}${url.REKRYTERING}${url.REK_AVSLUTADE}`}
          >
            <span>Avslutade rekryteringar {closedCount && <span className="count">({closedCount})</span>}</span>
            {fetchMoreClosedButton}
            {closedSpinner}
            {view === url.REK_AVSLUTADE ? <ArrowDown /> : <ArrowUp />}
          </Header>
          {
            isClosedView && (
              <RecruitmentList
                recruitments={closedData?.dashboardRecruitments || []}
                loading={closedLoading}
              />
            )}
        </Content>
        <Filter
          onClick={toggleFilter}
          onChange={onFilter}
        />
      </Wrapper>
    </MainLayout>
  )
}

export default Overview